import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';
import {
  FaTimes,
  FaMapMarkerAlt,
  FaUser,
  FaPhone,
  FaEnvelope,
  FaCalendarAlt,
  FaUndo,
  FaEdit,
  FaSave,
  FaExternalLinkAlt,
  FaHistory,
  FaCheckCircle,
  FaArrowRight
} from 'react-icons/fa';

const ExpiredClientDetailModal = ({ client, onClose, onRefresh }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isRevoking, setIsRevoking] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);
  const [detailedClient, setDetailedClient] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(true);
  const [formData, setFormData] = useState({
    name: client.name || '',
    address: client.address || '',
    pinCode: client.pinCode || '',
    location: client.location || '',
    state: client.state || '',
    contact_person: client.contact_person || '',
    phone: client.phone || '',
    email: client.email || '',
    remarks: client.remarks || '',
    closerPercentage: client.closerPercentage || ''
  });

  // Fetch detailed client info including stage history
  useEffect(() => {
    const fetchDetailedClient = async () => {
      try {
        setLoadingDetails(true);
        const response = await axios.get(`/api/expired-clients/${client.id}`);
        setDetailedClient(response.data.expiredClient);
      } catch (error) {
        console.error('Error fetching detailed client:', error);
        // Fall back to the basic client data
        setDetailedClient(client);
      } finally {
        setLoadingDetails(false);
      }
    };

    fetchDetailedClient();
  }, [client.id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleUpdate = async () => {
    setIsSaving(true);
    try {
      await axios.put(`/api/expired-clients/${client.id}`, formData);
      toast.success('Client details updated successfully!');
      setIsEditing(false);
      onRefresh();
    } catch (error) {
      console.error('Error updating client:', error);
      toast.error(error.response?.data?.error || 'Failed to update client details');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRevoke = async () => {
    setIsRevoking(true);
    try {
      await axios.post(`/api/expired-clients/${client.id}/revoke`);
      toast.success('Gym restored successfully! It has been moved back to the pipeline.');
      onRefresh();
      onClose();
    } catch (error) {
      console.error('Error revoking client:', error);
      toast.error(error.response?.data?.error || 'Failed to restore gym');
    } finally {
      setIsRevoking(false);
      setShowRestoreConfirm(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getExpirationReasonLabel = (reason) => {
    switch (reason) {
      case 'visit_limit_exceeded':
        return 'Visit Limit Exceeded (3+ visits without meeting owner)';
      case 'manual_deletion':
        return 'Manually Deleted';
      case 'internal_rejection':
        return 'Internal Rejection (Proposal Rejected)';
      default:
        return reason;
    }
  };

  const getExpirationReasonColor = (reason) => {
    switch (reason) {
      case 'visit_limit_exceeded':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'manual_deletion':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'internal_rejection':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStageColor = (stage) => {
    const colors = {
      'New Lead': 'bg-gray-500',
      'Site Visit': 'bg-blue-500',
      'Proposal Sent': 'bg-yellow-500',
      'Agreement Signed': 'bg-green-500',
      'Facility Work': 'bg-purple-500',
      'Post Reki Approved': 'bg-indigo-500',
      'Gym Onboard': 'bg-red-500'
    };
    return colors[stage] || 'bg-gray-500';
  };

  const getStageBadgeColor = (stage) => {
    const colors = {
      'New Lead': 'bg-gray-100 text-gray-800',
      'Site Visit': 'bg-blue-100 text-blue-800',
      'Proposal Sent': 'bg-yellow-100 text-yellow-800',
      'Agreement Signed': 'bg-green-100 text-green-800',
      'Facility Work': 'bg-purple-100 text-purple-800',
      'Post Reki Approved': 'bg-indigo-100 text-indigo-800',
      'Gym Onboard': 'bg-red-100 text-red-800'
    };
    return colors[stage] || 'bg-gray-100 text-gray-800';
  };

  // Use detailed client if available, otherwise fall back to basic client
  const displayClient = detailedClient || client;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                {isEditing ? 'Edit Expired Client' : 'Expired Client Details'}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Original Gym ID: {client.originalGymId}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <FaTimes className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-180px)] p-6">
          {/* Expiration Info Banner */}
          <div className={`mb-6 p-4 rounded-lg border ${getExpirationReasonColor(client.expirationReason)}`}>
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold">Expiration Reason</h3>
                <p className="text-sm mt-1">{getExpirationReasonLabel(client.expirationReason)}</p>
              </div>
              <div className="text-right">
                <p className="text-sm font-medium">Expired On</p>
                <p className="text-sm">{formatDate(client.expiredAt)}</p>
              </div>
            </div>
            <div className="mt-2 text-sm">
              <span className="font-medium">Total Visits:</span> {client.totalVisits || 0}
              {client.lastVisitDate && (
                <span className="ml-4">
                  <span className="font-medium">Last Visit:</span> {formatDate(client.lastVisitDate)}
                </span>
              )}
            </div>

            {/* Internal Rejection Details */}
            {client.expirationReason === 'internal_rejection' && client.rejectionReason && (
              <div className="mt-4 pt-4 border-t border-purple-200">
                <h4 className="font-semibold text-purple-800 mb-2">Rejection Details</h4>
                <div className="bg-white bg-opacity-60 p-3 rounded-md">
                  <p className="text-sm text-purple-700">
                    <span className="font-medium">Reason:</span> {client.rejectionReason}
                  </p>
                  {client.rejectedBy && (
                    <p className="text-sm text-purple-600 mt-2">
                      <span className="font-medium">Rejected By:</span> {client.rejectedBy.name}
                      {client.rejectedBy.role?.name && (
                        <span className="text-purple-500"> ({client.rejectedBy.role.name})</span>
                      )}
                    </p>
                  )}
                  {client.rejectedAt && (
                    <p className="text-sm text-purple-600 mt-1">
                      <span className="font-medium">Rejected On:</span> {formatDate(client.rejectedAt)}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Main Details Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left Column - Gym Details */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Gym Information</h3>

              {isEditing ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Gym Name</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                    <textarea
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                      <input
                        type="text"
                        name="location"
                        value={formData.location}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                      <input
                        type="text"
                        name="state"
                        value={formData.state}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Pin Code</label>
                    <input
                      type="text"
                      name="pinCode"
                      value={formData.pinCode}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <p className="text-sm text-gray-500">Gym Name</p>
                    <p className="font-medium text-gray-900">{client.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Address</p>
                    <p className="font-medium text-gray-900">{client.address || 'N/A'}</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <FaMapMarkerAlt className="text-gray-400 mt-1" />
                    <div>
                      <p className="font-medium text-gray-900">{client.location || 'N/A'}, {client.state || 'N/A'}</p>
                      <p className="text-sm text-gray-500">Pin Code: {client.pinCode || 'N/A'}</p>
                    </div>
                  </div>
                  {client.google_maps_url && (
                    <a
                      href={client.google_maps_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800"
                    >
                      <FaExternalLinkAlt className="w-3 h-3" />
                      View on Google Maps
                    </a>
                  )}
                  {(client.gps_lat && client.gps_lng) && (
                    <div>
                      <p className="text-sm text-gray-500">GPS Coordinates</p>
                      <p className="font-medium text-gray-900">{client.gps_lat}, {client.gps_lng}</p>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Right Column - Contact Details */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Contact Information</h3>

              {isEditing ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Contact Person</label>
                    <input
                      type="text"
                      name="contact_person"
                      value={formData.contact_person}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <input
                      type="text"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <FaUser className="text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">Contact Person</p>
                      <p className="font-medium text-gray-900">{client.contact_person || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <FaPhone className="text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">Phone</p>
                      <p className="font-medium text-gray-900">{client.phone || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <FaEnvelope className="text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">Email</p>
                      <p className="font-medium text-gray-900">{client.email || 'N/A'}</p>
                    </div>
                  </div>
                </>
              )}

              {/* Assigned User */}
              <div className="mt-4 pt-4 border-t">
                <h4 className="text-md font-semibold text-gray-900 mb-2">Assigned To</h4>
                {client.assignedUser ? (
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                      <FaUser className="text-gray-500" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{client.assignedUser.name}</p>
                      <p className="text-sm text-gray-500">{client.assignedUser.role?.name}</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-500">Unassigned</p>
                )}
              </div>
            </div>
          </div>

          {/* Additional Information */}
          <div className="mt-6 pt-6 border-t">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Additional Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-500">Last Stage</p>
                <p className="font-medium text-gray-900">{client.stage || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Closer Percentage</p>
                {isEditing ? (
                  <input
                    type="number"
                    name="closerPercentage"
                    value={formData.closerPercentage}
                    onChange={handleInputChange}
                    min="0"
                    max="100"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  />
                ) : (
                  <p className="font-medium text-gray-900">
                    {client.closerPercentage ? `${client.closerPercentage}%` : 'N/A'}
                  </p>
                )}
              </div>
              <div>
                <p className="text-sm text-gray-500">Original Created Date</p>
                <p className="font-medium text-gray-900">{formatDate(client.originalCreatedAt)}</p>
              </div>
            </div>

            {/* Remarks */}
            <div className="mt-4">
              <p className="text-sm text-gray-500 mb-1">Remarks</p>
              {isEditing ? (
                <textarea
                  name="remarks"
                  value={formData.remarks}
                  onChange={handleInputChange}
                  rows={3}
                  placeholder="Add any notes or remarks..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              ) : (
                <p className="font-medium text-gray-900 bg-gray-50 p-3 rounded-lg">
                  {client.remarks || 'No remarks'}
                </p>
              )}
            </div>
          </div>

          {/* Stage History / Timeline Section */}
          {loadingDetails ? (
            <div className="mt-6 pt-6 border-t">
              <div className="flex items-center gap-2 mb-4">
                <FaHistory className="text-indigo-500" />
                <h3 className="text-lg font-semibold text-gray-900">Stage History & Timeline</h3>
              </div>
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              </div>
            </div>
          ) : displayClient.stageHistory && displayClient.stageHistory.length > 0 ? (
            <div className="mt-6 pt-6 border-t">
              <div className="flex items-center gap-2 mb-4">
                <FaHistory className="text-indigo-500" />
                <h3 className="text-lg font-semibold text-gray-900">Stage History & Timeline</h3>
              </div>

              {/* Stage Progress Visualization */}
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-indigo-700">Journey Progress</span>
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStageBadgeColor(client.stage)}`}>
                    Last Stage: {client.stage}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  {['New Lead', 'Site Visit', 'Proposal Sent', 'Agreement Signed', 'Facility Work', 'Post Reki Approved', 'Gym Onboard'].map((stage, index) => {
                    const stageReached = displayClient.stageHistory.some(h => h.toStage === stage);
                    const isLastStage = client.stage === stage;
                    return (
                      <div key={stage} className="flex items-center flex-1">
                        <div
                          className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                            isLastStage
                              ? `${getStageColor(stage)} text-white ring-2 ring-offset-1 ring-red-300`
                              : stageReached
                              ? `${getStageColor(stage)} text-white`
                              : 'bg-gray-200 text-gray-400'
                          }`}
                          title={stage}
                        >
                          {stageReached || isLastStage ? (
                            <FaCheckCircle className="w-3 h-3" />
                          ) : (
                            index + 1
                          )}
                        </div>
                        {index < 6 && (
                          <div className={`flex-1 h-0.5 mx-0.5 ${stageReached ? 'bg-indigo-300' : 'bg-gray-200'}`} />
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="flex justify-between mt-1 text-xs text-gray-500">
                  <span>Start</span>
                  <span>Expired at: {client.stage}</span>
                </div>
              </div>

              {/* Timeline */}
              <div className="relative">
                <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-indigo-200"></div>
                <div className="space-y-4">
                  {displayClient.stageHistory.map((history, index) => (
                    <div key={history.id} className="relative pl-10">
                      <div className={`absolute left-2 w-5 h-5 rounded-full flex items-center justify-center ${getStageColor(history.toStage)}`}>
                        <FaCheckCircle className="w-3 h-3 text-white" />
                      </div>
                      <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            {history.fromStage ? (
                              <>
                                <span className={`px-2 py-0.5 text-xs font-medium rounded ${getStageBadgeColor(history.fromStage)}`}>
                                  {history.fromStage}
                                </span>
                                <FaArrowRight className="w-3 h-3 text-gray-400" />
                              </>
                            ) : (
                              <span className="text-xs text-gray-500">Created at</span>
                            )}
                            <span className={`px-2 py-0.5 text-xs font-medium rounded ${getStageBadgeColor(history.toStage)}`}>
                              {history.toStage}
                            </span>
                          </div>
                          <span className="text-xs text-gray-500">
                            {formatDate(history.changedAt)}
                          </span>
                        </div>
                        <div className="flex items-center gap-1 text-sm text-gray-600">
                          <FaUser className="w-3 h-3" />
                          <span>{history.changer?.name || 'Unknown'}</span>
                          {history.changer?.role?.name && (
                            <span className="text-gray-400">({history.changer.role.name})</span>
                          )}
                        </div>
                        {history.comments && (
                          <p className="mt-2 text-sm text-gray-500 bg-gray-50 p-2 rounded">
                            {history.comments}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}

                  {/* Expiration Entry */}
                  <div className="relative pl-10">
                    <div className="absolute left-2 w-5 h-5 rounded-full flex items-center justify-center bg-red-500">
                      <FaTimes className="w-3 h-3 text-white" />
                    </div>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="px-2 py-0.5 text-xs font-medium rounded bg-red-100 text-red-800">
                          Expired / Moved to Expired Clients
                        </span>
                        <span className="text-xs text-gray-500">
                          {formatDate(client.expiredAt)}
                        </span>
                      </div>
                      <p className="text-sm text-red-700">
                        Reason: {getExpirationReasonLabel(client.expirationReason)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-6 pt-6 border-t">
              <div className="flex items-center gap-2 mb-4">
                <FaHistory className="text-indigo-500" />
                <h3 className="text-lg font-semibold text-gray-900">Stage History & Timeline</h3>
              </div>
              <div className="bg-gray-50 rounded-lg p-6 text-center">
                <FaHistory className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No stage history available for this gym.</p>
                <p className="text-sm text-gray-400 mt-1">
                  Stage history may have been removed when the gym was expired.
                </p>
              </div>
            </div>
          )}

          {/* Visit Logs Section */}
          {displayClient.visitLogs && displayClient.visitLogs.length > 0 && (
            <div className="mt-6 pt-6 border-t">
              <div className="flex items-center gap-2 mb-4">
                <FaCalendarAlt className="text-blue-500" />
                <h3 className="text-lg font-semibold text-gray-900">Visit History ({displayClient.visitLogs.length} visits)</h3>
              </div>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {displayClient.visitLogs.map((visit, index) => (
                  <div key={visit.id} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-sm font-semibold text-blue-600">{index + 1}</span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {visit.user?.name || 'Unknown'}
                          {visit.user?.role?.name && (
                            <span className="text-gray-400 ml-1">({visit.user.role.name})</span>
                          )}
                        </p>
                        <p className="text-xs text-gray-500">
                          {visit.ownerMet ? '✅ Owner Met' : '❌ Owner Not Met'}
                          {visit.notes && ` - ${visit.notes.substring(0, 50)}${visit.notes.length > 50 ? '...' : ''}`}
                        </p>
                      </div>
                    </div>
                    <span className="text-xs text-gray-500">
                      {formatDate(visit.visitDate)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex justify-between items-center">
            <button
              onClick={() => setShowRestoreConfirm(true)}
              disabled={isRevoking}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FaUndo />
              {isRevoking ? 'Restoring...' : 'Restore to Pipeline'}
            </button>

            <div className="flex gap-3">
              {isEditing ? (
                <>
                  <button
                    onClick={() => setIsEditing(false)}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpdate}
                    disabled={isSaving}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FaSave />
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => setIsEditing(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    <FaEdit />
                    Edit Details
                  </button>
                  <button
                    onClick={onClose}
                    className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    Close
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Restore Confirmation Modal */}
      {showRestoreConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <FaUndo className="text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Restore Gym to Pipeline</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Are you sure you want to restore <span className="font-semibold">{client.name}</span> back to active status? It will be moved back to the pipeline.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowRestoreConfirm(false)}
                disabled={isRevoking}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleRevoke}
                disabled={isRevoking}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {isRevoking ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Restoring...
                  </>
                ) : (
                  <>
                    <FaUndo />
                    Yes, Restore
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpiredClientDetailModal;
